import { css } from "@emotion/css";
import { Button, ExternalLink, Select } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentV2Resource, ProjectResource, TenantProjectEnvironmentDetail } from "@octopusdeploy/octopus-server-client";
import React, { useState } from "react";
import CopyValueToClipboard from "~/components/CopyToClipboardButton/CopyValueToClipboard";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import { ConfigurableEnvironmentSelect } from "./ConfigurableEnvironmentSelect";
interface ClientIdentifierDialogProps {
    project: ProjectResource;
    allEnvironments: EnvironmentV2Resource[];
    configurableEnvironments: EnvironmentV2Resource[];
}
const styles = {
    // Apply gap between form elements until this is built into the components.
    formElementContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
};
export function PreviewClientIdentifierDialog({ project, allEnvironments, configurableEnvironments }: ClientIdentifierDialogProps) {
    const [environmentId, setEnvironmentId] = useState<string | undefined>(configurableEnvironments.length > 0 ? configurableEnvironments[0].Id : undefined);
    const [tenantId, setTenantId] = useState<string>();
    const { error: getTenantsError, isLoading: getTenantsBusy, result: tenants, } = useInlineStatusQuery(async (repository) => {
        if (!environmentId) {
            return [];
        }
        return await repository.FeatureToggles.getTenantsConnectedToProjectAndEnvironment(project, environmentId);
    }, [project, environmentId], "Get Tenants And Environments for Client Identifier Preview");
    const { error: getClientIdentifierError, isLoading: getClientIdentifierBusy, result: clientIdentifier, refetch: regenerateClientIdentifier, } = useInlineStatusQuery(async (repository) => {
        if (!environmentId) {
            return undefined;
        }
        const response = await repository.FeatureToggles.generateClientIdentifier(project, environmentId, tenantId);
        return response.ClientIdentifier;
    }, [project, environmentId, tenantId], "Get Client Identifier Preview");
    function getErrors() {
        const errors = [];
        if (getTenantsError) {
            errors.push(getTenantsError);
        }
        if (getClientIdentifierError) {
            errors.push(getClientIdentifierError);
        }
        return errors;
    }
    function isEphemeralEnvironment(environmentId: string | undefined): boolean {
        if (!environmentId) {
            return false;
        }
        const environment = allEnvironments.find((e) => e.Id === environmentId);
        return environment?.Type === "Ephemeral";
    }
    return (<OkDialogLayout title={"Preview Client Identifiers"} busy={getTenantsBusy || getClientIdentifierBusy} errors={getErrors()} onOkClick={() => { }} hideCancel={true}>
            <React.Fragment>
                <>
                    <p>Client identifiers allow your software to access Octopus Feature Toggles.</p>
                    <p>
                        Octopus provides the <b>Octopus.FeatureToggles.ClientIdentifier</b> deployment variable for you to merge into your application configuration.
                    </p>
                    <p>If you wish to use Octopus Feature Toggles in applications not deployed by Octopus, or wish to otherwise provide the client identifier to your client software, you can generate an appropriate identifier below.</p>
                    <p>Note that using this generated token will not resolve any toggles that have minimum release versions defined.</p>
                    <div className={styles.formElementContainer}>
                        <ConfigurableEnvironmentSelect allEnvironments={allEnvironments} configurableEnvironments={configurableEnvironments} currentEnvironmentId={environmentId} onChange={setEnvironmentId}/>
                        {!isEphemeralEnvironment(environmentId) && tenants && tenants.length > 0 && (<Select<TenantProjectEnvironmentDetail> label={"Tenant"} sortItems={false} value={tenantId} items={tenants} getOption={(tenant) => ({ value: tenant.TenantId, label: tenant.Name })} onChange={(selected) => {
                setTenantId(selected);
            }} allowClear={true}/>)}
                        <Button label={"Preview"} onClick={() => regenerateClientIdentifier()} importance="primary" disabled={!environmentId}/>
                    </div>
                    {clientIdentifier && (<div>
                            <p>
                                Your client identifier for <b>{allEnvironments.find((x) => x.Id === environmentId)?.Name}</b>
                                {tenants && tenantId ? (<>
                                        {" "}
                                        and <b>{tenants.find((x) => x.TenantId === tenantId)?.Name}</b>
                                    </>) : null}{" "}
                                is:
                            </p>
                            <CopyValueToClipboard value={clientIdentifier}>
                                <p style={{ maxWidth: 540, wordWrap: "break-word" }}>{clientIdentifier}</p>
                            </CopyValueToClipboard>
                            <ExternalLink href={"https://oc.to/UsingClientIdentifiers"}>Using Feature Toggle Client Identifiers</ExternalLink>.
                        </div>)}
                </>
            </React.Fragment>
        </OkDialogLayout>);
}
